import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSClasses: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Classes</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    A blueprint for creating objects that bundle state and behavior — clean syntax on top of JavaScript&apos;s prototype system, with constructors, inheritance, static members, getters/setters, and private fields.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="16 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>In a food-delivery app you must represent users, orders, restaurants, payments, drivers. Hand-building each object duplicates the same shape and behavior over and over:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user1 = { id: 1, name: "Sachin", login() { console.log("Logged In"); } };\nconst user2 = { id: 2, name: "Rahul",  login() { console.log("Logged In"); } };\nconst user3 = { id: 3, name: "Amit",   login() { console.log("Logged In"); } };` }]} defaultLanguage="javascript" id="cl-problem" />
   <Para>At 10,000 users this is unmaintainable. We need to define <Mark>structure + behavior</Mark> once and stamp out many objects from it. That&apos;s a class.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">A <Mark>class</Mark> is a blueprint for creating objects — it defines properties (state) and methods (behavior). The objects created from it are <Mark>instances</Mark>.</p>
   </div>
   <Para>Class = blueprint. Object = the actual building made from it.</Para>
  </section>

  {/* 3. Why */}
  <section className="mb-12">
   <SectionHeading n={3}>Why Classes Exist</SectionHeading>
   <Para>Define once, instantiate many — no duplication:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `class User {}\n\nnew User();\nnew User();\nnew User();` }]} defaultLanguage="javascript" id="cl-why" />
   <div className="flex flex-wrap gap-2">{['Reusability', 'Maintainability', 'Scalability', 'Cleaner design'].map((b) => <Pill key={b}>{b}</Pill>)}</div>
  </section>

  {/* 4. Core idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea — Blueprint</SectionHeading>
   <Para>A class is a factory blueprint. The blueprint lists the properties (<Code>id</Code>, <Code>name</Code>) and methods (<Code>login()</Code>, <Code>logout()</Code>); every object you create follows it.</Para>
  </section>

  {/* 5. Creating + new */}
  <section className="mb-12">
   <SectionHeading n={5}>Creating a Class &amp; Instances</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `class User {}        // definition — no objects yet\n\nconst user = new User(); // 'new' creates an instance` }]} defaultLanguage="javascript" id="cl-create" />
  </section>

  {/* 6. Constructor */}
  <section className="mb-12">
   <SectionHeading n={6}>The Constructor</SectionHeading>
   <Para>The <Code>constructor</Code> runs automatically when you call <Code>new</Code> — it&apos;s the &quot;birth process&quot; that initializes each instance:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `class User {\n  constructor(id, name) {\n    this.id = id;\n    this.name = name;\n  }\n}\n\nconst user = new User(1, "Sachin"); // { id: 1, name: "Sachin" }` }]} defaultLanguage="javascript" id="cl-constructor" />
  </section>

  {/* 7. this */}
  <section className="mb-12">
   <SectionHeading n={7}>Understanding this</SectionHeading>
   <Para>Inside a class, <Code>this</Code> refers to the current instance being created or acted on:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `class User {\n  constructor(name) {\n    this.name = name; // sets name on this specific object\n  }\n}\n\nconst user = new User("Sachin"); // user.name === "Sachin"` }]} defaultLanguage="javascript" id="cl-this" />
  </section>

  {/* 8. Instance methods + multiple objects */}
  <section className="mb-12">
   <SectionHeading n={8}>Instance Methods &amp; Multiple Objects</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `class User {\n  constructor(name) {\n    this.name = name;\n  }\n  login() {\n    console.log(\`\${this.name} logged in\`);\n  }\n}\n\nconst u1 = new User("Sachin");\nconst u2 = new User("Rahul");\nu1.login(); // "Sachin logged in"` }]} defaultLanguage="javascript" id="cl-methods" />
   <Output>Sachin logged in</Output>
   <Callout tone="info">Each object stores its <Mark>own data</Mark>, but the <Code>login</Code> method is <Mark>shared</Mark> on the prototype — so methods aren&apos;t duplicated per object (memory-efficient).</Callout>
  </section>

  {/* 9. Behind the scenes */}
  <section className="mb-12">
   <SectionHeading n={9}>Behind the Scenes: Syntactic Sugar</SectionHeading>
   <Para>JavaScript classes are <Mark>syntactic sugar over prototypes</Mark>. A method defined in a class actually lives on the prototype, not inside each instance:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `class User {\n  login() {}\n}\n\n// internally equivalent to:\nUser.prototype.login = function () {};` }]} defaultLanguage="javascript" id="cl-prototype" />
   <Callout tone="info">A favorite interview point: classes don&apos;t replace prototypes — they <Mark>use</Mark> them under the hood.</Callout>
  </section>

  {/* 10. Static methods + fields */}
  <section className="mb-12">
   <SectionHeading n={10}>Static Methods &amp; Fields</SectionHeading>
   <Para>Mark behavior that belongs to the <Mark>class itself</Mark> (not an instance) with <Code>static</Code> — call it on the class, not an object:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `class MathUtil {\n  static add(a, b) {\n    return a + b;\n  }\n}\n\nMathUtil.add(10, 20); // 30\n// new MathUtil().add() -> would fail` }]} defaultLanguage="javascript" id="cl-static" />
   <Para>Static fields are shared across all instances — perfect for a counter:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `class User {\n  static count = 0;\n  constructor(name) {\n    this.name = name;\n    User.count++;\n  }\n}\n\nnew User("A"); new User("B"); new User("C");\nconsole.log(User.count); // 3` }]} defaultLanguage="javascript" id="cl-staticfield" />
   <Output>3</Output>
  </section>

  {/* 11. Getters & setters */}
  <section className="mb-12">
   <SectionHeading n={11}>Getters &amp; Setters</SectionHeading>
   <Para>Accessors look like properties but run code — great for derived values and validation:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `class User {\n  constructor(first, last) {\n    this.first = first;\n    this.last = last;\n  }\n  get fullName() {\n    return \`\${this.first} \${this.last}\`;\n  }\n  set name(value) {\n    if (!value) throw new Error("Invalid Name");\n    this._name = value;\n  }\n}\n\nconst u = new User("Sachin", "Saini");\nu.fullName; // "Sachin Saini"  (no parentheses)` }]} defaultLanguage="javascript" id="cl-getset" />
  </section>

  {/* 12. Inheritance */}
  <section className="mb-12">
   <SectionHeading n={12}>Inheritance (extends)</SectionHeading>
   <Para>One class can inherit state and behavior from another with <Code>extends</Code> — avoiding duplicating shared logic like <Code>login()</Code> across Admin, Driver, Customer:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `class User {\n  login() {\n    console.log("Login");\n  }\n}\n\nclass Admin extends User {}\n\nnew Admin().login(); // "Login" — inherited` }]} defaultLanguage="javascript" id="cl-inherit" />
   <pre className="text-[13px] font-mono text-gray-600 dark:text-gray-400">{`User\n ├── Admin\n ├── Driver\n └── Customer`}</pre>
  </section>

  {/* 13. super */}
  <section className="mb-12">
   <SectionHeading n={13}>super()</SectionHeading>
   <Para>A child constructor must call <Code>super()</Code> (the parent constructor) <Mark>before</Mark> using <Code>this</Code>:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `class User {\n  constructor(name) {\n    this.name = name;\n  }\n}\n\nclass Admin extends User {\n  constructor(name, role) {\n    super(name);     // must run first\n    this.role = role;\n  }\n}\n\nnew Admin("Sachin", "SUPER_ADMIN");` }]} defaultLanguage="javascript" id="cl-super" />
   <Callout tone="warning">Using <Code>this</Code> before <Code>super()</Code> in a derived constructor throws a <Code>ReferenceError</Code>.</Callout>
  </section>

  {/* 14. Method overriding */}
  <section className="mb-12">
   <SectionHeading n={14}>Method Overriding</SectionHeading>
   <Para>A child can replace a parent method by redefining it:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `class User {\n  login() { console.log("User Login"); }\n}\n\nclass Admin extends User {\n  login() { console.log("Admin Login"); } // overrides\n}\n\nnew Admin().login(); // "Admin Login"` }]} defaultLanguage="javascript" id="cl-override" />
   <Output>Admin Login</Output>
  </section>

  {/* 15. Private fields */}
  <section className="mb-12">
   <SectionHeading n={15}>Private Fields (#)</SectionHeading>
   <Para>Prefix a field with <Code>#</Code> to make it truly private — inaccessible outside the class:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `class BankAccount {\n  #balance = 0;\n  deposit(amount) {\n    this.#balance += amount;\n  }\n  get balance() {\n    return this.#balance;\n  }\n}\n\nconst acc = new BankAccount();\nacc.deposit(100);\nacc.#balance; // SyntaxError — genuinely private` }]} defaultLanguage="javascript" id="cl-private" />
   <Callout tone="info">Before <Code>#</Code>, a leading underscore (<Code>_balance</Code>) was just a convention — anyone could still read it. <Code>#</Code> is enforced by the language.</Callout>
  </section>

  {/* 16. Across languages */}
  <section className="mb-12">
   <SectionHeading n={16}>Across Languages</SectionHeading>
   <CodeBlock snippets={[{ language: 'typescript', code: `class User {\n  private id: number;\n  constructor(id: number, public name: string) {\n    this.id = id;\n  }\n  login(): void {\n    console.log("Login");\n  }\n}` }]} defaultLanguage="typescript" id="cl-ts" />
   <CodeBlock snippets={[{ language: 'java', code: `class User {\n  private String name;\n  User(String name) { this.name = name; }\n  void login() { System.out.println("Login"); }\n}` }]} defaultLanguage="java" id="cl-java" />
   <Callout tone="info">JS class syntax intentionally resembles Java/C++ — but remember it&apos;s prototype-based underneath, and TypeScript adds real visibility modifiers and type checks.</Callout>
  </section>

  {/* 17. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={17}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="Forgetting new"><Code>User()</Code> without <Code>new</Code> won&apos;t create an instance (and throws in strict mode).</Callout>
   <Callout tone="warning" title="Using this before super()">In a derived class, call <Code>super()</Code> first.</Callout>
   <Callout tone="warning" title="Static vs instance confusion">Static members are called on the class; instance members on objects.</Callout>
   <Callout tone="warning" title="Thinking classes replace prototypes">They&apos;re sugar over prototypes, not a replacement.</Callout>
   <Callout tone="warning" title="Overusing inheritance">Deep hierarchies get rigid — modern designs often prefer <Mark>composition over inheritance</Mark>.</Callout>
  </section>

  {/* 18. Real interview questions */}
  <section className="mb-12">
   <SectionHeading n={18}>Real Interview Questions</SectionHeading>
   <QA q="What is the difference between a class and an object?">
    A class is the blueprint (structure + behavior); an object is a concrete instance created from it with <Code>new</Code>.
   </QA>
   <QA q="Are JavaScript classes truly object-oriented, or syntactic sugar?">
    They&apos;re syntactic sugar over the prototype system — methods live on <Code>Class.prototype</Code>, and inheritance uses the prototype chain.
   </QA>
   <QA q="Difference between static and instance methods?">
    Static methods belong to the class and are called as <Code>Class.method()</Code>; instance methods belong to objects and use <Code>this</Code>.
   </QA>
   <QA q="What does super() do and when must it be called?">
    It invokes the parent constructor; in a derived class it must run <em>before</em> any use of <Code>this</Code>.
   </QA>
   <QA q="How do private fields differ from the underscore convention?">
    <Code>#field</Code> is enforced by the engine — accessing it outside the class is a syntax error. <Code>_field</Code> is only a naming hint.
   </QA>
   <QA q="Composition vs inheritance — which to prefer?">
    Favor composition (assembling behavior from small pieces) for flexibility; reserve inheritance for genuine &quot;is-a&quot; relationships.
   </QA>
  </section>

  {/* 19. Mental model */}
  <section className="mb-12">
   <SectionHeading n={19}>Mental Model</SectionHeading>
   <Para>Think of a car factory. The <Mark>blueprint</Mark> defines color, engine, <Code>start()</Code>, <Code>stop()</Code>; the factory stamps out Car #1, Car #2, Car #3. Class = blueprint, object = the actual product.</Para>
  </section>

  {/* 20 & 21 */}
  <section className="mb-4">
   <SectionHeading n={20}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     A class is a blueprint for creating objects that encapsulates state and behavior, providing cleaner syntax over JavaScript&apos;s prototype-based inheritance.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Classes give you constructors, methods, inheritance, static members, and encapsulation in a familiar syntax — but they&apos;re built on prototypes. For interviews, understand both the class syntax and the prototype mechanism behind it.
   </Callout>
  </section>
 </div>
);

export default JSClasses;
